import { useCallback } from 'react';
import { Guides, MobileBFFGuide } from '@/types/guides';
import type { Location } from '@/types';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';

const emptyGuides: Guides = {
    items: [],
    totalSize: 0,
    pageSize: 0,
};

const fetchGuides = async (place: Location) => {
    if (!place.slug || place.isOnline) {
        return emptyGuides;
    }

    try {
        const response = await fetch(`/home/api/guides/?slug=${place.slug}`);
        if (!response.ok) {
            throw new Error('Failed to fetch guides');
        }
        return (await response.json()) as Guides;
    } catch (e) {
        console.error('Error fetching guides', e);
        throw e;
    }
};

interface UseGuidesProps extends UseQueryOptions<Guides, Error> {
    /**
     * The the place object to fetch guides for.
     */
    place: Location;
    /**
     * A boolean indicating if the guides should be fetched.
     */
    showGuides?: boolean;
}
export function useGuidesQuery({ place, showGuides, ...options }: UseGuidesProps) {
    const enabled = showGuides && !!place && place.slug !== '';

    return useQuery({
        queryKey: ['guides', place],
        queryFn: () => fetchGuides(place),
        enabled,
        staleTime: options.initialData ? 0 : undefined,
        ...options,
    });
}

/**
 * Fetches guides from the API and filters out any guides that are in the guidesToExclude array.
 * @returns An object containing the fetched guides and a boolean indicating if the guides are still loading.
 */
export function useFilteredGuides({
    guidesToExclude = [],
    ...options
}: UseGuidesProps & {
    /**
     * An array of guideIds to exclude from the fetched guides.
     */
    guidesToExclude?: string[];
}) {
    const filterGuides = useCallback(
        (data: Guides) => {
            return {
                ...data,
                items: data.items.filter(
                    (guide: MobileBFFGuide) => !guidesToExclude.includes(guide.guideId),
                ),
            };
        },
        [guidesToExclude],
    );

    const response = useGuidesQuery({ showGuides: true, ...options, select: filterGuides });

    return response;
}

export const useGuides = useFilteredGuides;
